import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollToTop } from '../hooks/useScrollToTop';
import toast from 'react-hot-toast';
import { forgotPasswordAPI } from '../services/forgotPasswordAPI';

const ForgotPassword: React.FC = () => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [step, setStep] = useState<'email' | 'verification' | 'password' | 'complete'>('email');

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  const [formData, setFormData] = useState({
    email: '',
  });

  const [verificationData, setVerificationData] = useState({
    code: '',
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVerificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationData({
      ...verificationData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error(t('email_required'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPasswordAPI.sendVerificationCode(formData);

      if (response.success) {
        setStep('verification');
        toast.success(t('verification_code_sent'));
      } else {
        toast.error(response.message || t('verification_code_failed'));
      }
    } catch (error) {
      const err = error as { code?: string; message?: string; response?: { status?: number; data?: { message?: string } } };

      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        toast.error(t('server_connection_error'));
      } else if (err.response?.status === 500) {
        const errorMessage = err.response?.data?.message || t('email_not_found_description');
        toast.error(errorMessage);
      } else if (err.response?.status && err.response.status >= 400) {
        toast.error(t('email_not_found_description'));
      } else {
        toast.error(t('verification_code_failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationData.code) {
      toast.error(t('verification_code_required'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPasswordAPI.verifyEmailCode({
        email: formData.email,
        code: verificationData.code
      });

      if (response.success) {
        setStep('password');
        toast.success(t('verification_complete'));
      } else {
        toast.error(response.message || t('verification_code_verification_failed'));
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('verification_code_verification_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);

    try {
      const response = await forgotPasswordAPI.sendVerificationCode(formData);

      if (response.success) {
        toast.success(t('verification_code_sent_description'));
        // 인증 코드 입력 필드 초기화
        setVerificationData({ code: '' });
      } else {
        toast.error(response.message || t('verification_code_resend_failed'));
      }
    } catch (error) {
      const err = error as { code?: string; message?: string; response?: { status?: number; data?: { message?: string } } };

      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        toast.error('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
      } else if (err.response?.status === 500) {
        const errorMessage = err.response?.data?.message || '존재하지 않은 계정이거나 등록되지 않은 이메일입니다. 관리자에게 문의해주세요.';
        toast.error(errorMessage);
      } else if (err.response?.status && err.response.status >= 400) {
        toast.error('존재하지 않은 계정이거나 등록되지 않은 이메일입니다. 관리자에게 문의해주세요.');
      } else {
        toast.error('인증 코드 재발송에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('password_mismatch_error'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('password_min_length_error'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPasswordAPI.resetPassword({
        email: formData.email,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        setStep('complete');
        toast.success(t('password_change_success'));
      } else {
        toast.error(response.message || t('password_reset_failed'));
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('password_reset_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'verification') {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-header">
            <Link to="/" className="forgot-password-title-link">
              <h1 className="forgot-password-title">DREAM<span className="logo-accent">DRUMS</span></h1>
            </Link>
            <p className="forgot-password-subtitle">인증 코드를 입력해주세요</p>
          </div>

          <div className="form-container">
            <form onSubmit={handleVerifyCode} className="forgot-password-form">
                <div className="form-group">
                  <label htmlFor="code" className="form-label">
                    {t('verification_code')}
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={verificationData.code}
                    onChange={handleVerificationChange}
                    className="form-input"
                    placeholder={t('verification_code_placeholder')}
                    required
                    maxLength={6}
                    pattern="[0-9]{6}"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary btn-md"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        {t('verifying')}
                      </>
                    ) : (
                      t('verify')
                    )}
                  </button>
                </div>

              <div className="forgot-password-footer">
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="btn btn-secondary"
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <span className="spinner"></span>
                      {t('resending')}
                    </>
                  ) : (
                    t('resend_code')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'password') {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-header">
            <Link to="/" className="forgot-password-title-link">
              <h1 className="forgot-password-title">DREAM<span className="logo-accent">DRUMS</span></h1>
            </Link>
            <p className="forgot-password-subtitle">새로운 비밀번호를 설정해주세요</p>
          </div>

          <div className="form-container">
            <form onSubmit={handlePasswordReset} className="forgot-password-form">
                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">
                    {t('new_password_label')}
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    placeholder={t('new_password_placeholder')}
                    required
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">
                    {t('confirm_password_label')}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    placeholder={t('confirm_password_placeholder')}
                    required
                    minLength={6}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary btn-md"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        {t('changing_password')}
                      </>
                    ) : (
                      t('password_change_complete')
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

  if (step === 'complete') {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-header">
            <Link to="/" className="forgot-password-title-link">
              <h1 className="forgot-password-title">DREAM<span className="logo-accent">DRUMS</span></h1>
            </Link>
            <p className="forgot-password-subtitle">{t('password_change_success_description')}</p>
          </div>

          <div className="form-container">
            <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>{t('password_change_success')}</h3>
                <p>
                  {t('password_change_success_description')}
                </p>
              </div>

            <div className="forgot-password-footer">
              <Link to="/login" className="btn btn-secondary">
                {t('back_to_login')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-header">
          <Link to="/" className="forgot-password-title-link">
            <h1 className="forgot-password-title">DREAM<span className="logo-accent">DRUMS</span></h1>
          </Link>
          <p className="forgot-password-subtitle">{t('forgot_password_subtitle')}</p>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  {t('email_label')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={t('email_placeholder')}
                  required
                />
              </div>

              <div className="form-note">
                <p>{t('email_note')}</p>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      {t('sending_verification_code')}
                    </>
                  ) : (
                    t('send_verification_code')
                  )}
                </button>
              </div>

            <div className="forgot-password-footer">
              <Link to="/login" className="back-to-login">
                {t('back_to_login')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
